import React, { useMemo } from 'react';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { useQuery } from '@apollo/client';
import { gql } from '@/modes/waterSupplier/gql';
import { ContactProfileInput } from '../Profile/types';
import Form from '../Form/Form';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import { Box, Flex, Link } from 'rebass';
import { SourceType } from '../WaterService/SourceTypePicker';
import { HazardLevel } from './HazardLevelPicker';
import { Label } from '@rebass/forms';
import WaterHazardsPicker, { WaterHazard } from './WaterHazardsPicker';
import { useJobFormData } from '../../hooks/useJobFormData';
import { AssemblyType } from '../BackflowAssembly/AssemblyTypePicker';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import useMode from '../../hooks/useMode';
import { AssemblyProtectionType } from '../BackflowAssembly/AssemblyProtectionPicker';
import DataTable from '../Table/DataTable';
import { Column } from '../../hooks/useTablePages';
import { PropertyType } from '../WaterService/PropertyTypePicker';
import ButtonToggle from '../Button/ButtonToggle';
import { JobFormDataBase } from '@ecosconnect/core/src/types/jobFormData';
import { GrowyTextarea } from '../Form/GrowyTextarea';
import Carousel, { CarouselInternalState } from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { UtilityIconButton } from '../Button/UtilityIconButton';

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
  },
};

const assembliesColumns: Column[] = [
  {
    header: 'Type',
    id: 'type',
    accessor: (row: ServiceAssembly) => {
      return (row.type || '').replace(/_/g, ' ');
    },
    width: 100,
  },
  {
    header: 'Protection Type',
    id: 'protectionType',
    accessor: (row: ServiceAssembly) => {
      return constantToLabel(row.protectionType);
    },
    width: 158,
  },
  {
    header: 'Make',
    accessor: 'model.backflowAssemblyMake.name',
    width: 130,
  },
  {
    header: 'Model',
    accessor: 'model.name',
    width: 130,
  },
  {
    header: 'Serial',
    accessor: 'serialNumber',
    width: 130,
  },
];

const LOAD_JOB = gql(`
  query GetHazardSurveyJob($where: JSON, $limit: Int) {
    jobs(where: $where, limit: $limit) {
      id
      formData
      address(required: true) {
        id
        street1
        street2
        city
        state
        zip
        zipExt
      }
      waterHazardSurvey(required: true) {
        jobId
        waterService(required: true) {
          id
          siteNumber
          meterNumber
          sourceType
          type
          backflowAssemblies {
            id
            hazardLevel
            serialNumber
            type
            protectionType
            model {
              name
              backflowAssemblyMake {
                name
              }
            }
          }
        }
        contact {
          id
          type
          name
          firstName
          lastName
          emails {
            subType
            value
          }
          phones {
            subType
            value
            extension
          }
        }
      }
    }
  }
`);

type WaterSiteEditModalProps = {
  onClose: () => void;
  jobId: string;
};

type HazardSurveyFormData = JobFormDataBase & {
  hazards: WaterHazard[] | readonly WaterHazard[];
  isProtected?: boolean;
  waterServices: Record<
    string,
    {
      hazards: WaterHazard[] | readonly WaterHazard[];
      isProtected?: boolean;
    }
  >;
};

type ServiceAssembly = {
  id: string;
  hazardLevel: HazardLevel;
  serialNumber: string;
  type: AssemblyType;
  protectionType: AssemblyProtectionType;
};

type JobWithAssessmentAndData = {
  id: string;
  formData: HazardSurveyFormData;
  address: {
    id: string;
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zip: string;
    zipExt: string;
  };
  waterHazardSurvey: {
    jobId: string;
    waterService: {
      id: string;
      meterNumber: string;
      siteNumber: string;
      sourceType: SourceType;
      type: PropertyType;
      backflowAssemblies: ServiceAssembly[];
    };
    contact?: ContactProfileInput;
    supervisor?: ContactProfileInput;
  };
};

const servicePrefix = 's';

const HazardSurveyResultsModal: React.FC<WaterSiteEditModalProps> = ({
  onClose,
  jobId,
}) => {
  const { error: itemError, data: itemData } = useQuery(LOAD_JOB, {
    variables: {
      where: {
        id: jobId,
      },
      limit: null,
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setFormValuesLocal((data.jobs[0] as any)?.formData);
    },
  });
  const waterService = itemData?.jobs?.[0]?.waterHazardSurvey?.waterService;
  const services = waterService ? [waterService] : [];

  const mode = useMode();

  const { setFormValuesLocal, formValues, setFormValue, onFormInputChange } =
    useJobFormData<HazardSurveyFormData>(jobId);

  const hasUnprotectedHazards = useMemo<boolean>(() => {
    const unprotectedSite =
      formValues?.hazards?.length && formValues.isProtected === false;

    const unprotectedService = Object.keys(
      formValues?.waterServices ?? {}
    ).some(
      (key) =>
        formValues?.waterServices?.[key]?.hazards?.length &&
        formValues?.waterServices?.[key]?.isProtected === false
    );

    return unprotectedSite || unprotectedService;
  }, [formValues]);

  const setHazards = (hazards: WaterHazard[] | readonly WaterHazard[]) => {
    setFormValue(
      'hazards',
      hazards.map((hazard) => ({
        id: hazard.id,
        name: hazard.name,
        description: hazard.description,
        hazardLevel: hazard.hazardLevel,
      }))
    );
  };

  const setIsProtected = (isProtected: boolean) => {
    setFormValue('isProtected', isProtected);
  };

  const setServiceHazards = (
    serviceId: string,
    hazards: WaterHazard[] | readonly WaterHazard[]
  ) => {
    setFormValue('waterServices', (oldValue) => ({
      ...oldValue,
      [servicePrefix + serviceId]: {
        ...oldValue?.[servicePrefix + serviceId],
        hazards: hazards.map((hazard) => ({
          id: hazard.id,
          name: hazard.name,
          description: hazard.description,
          hazardLevel: hazard.hazardLevel,
        })),
      },
    }));
  };

  const setServiceIsProtected = (serviceId: string, isProtected: boolean) => {
    setFormValue('waterServices', (oldValue) => ({
      ...oldValue,
      [servicePrefix + serviceId]: {
        ...oldValue?.[servicePrefix + serviceId],
        isProtected,
      },
    }));
  };

  const ButtonGroup: React.FC<{
    next?: () => void;
    previous?: () => void;
    carouselState?: CarouselInternalState;
  }> = ({ next, previous, carouselState }) => {
    const currentIndex = carouselState?.currentSlide ?? 0;

    if (carouselState!.totalItems < 2) {
      return <></>;
    }
    return (
      <Flex
        sx={{
          p: 2,
          backgroundColor: '#888',
          color: '#fff',
        }}
        alignItems="center"
        justifyContent="space-between"
      >
        <UtilityIconButton
          dark
          icon="chevron-left"
          disabled={currentIndex === 0}
          sx={{ visibility: currentIndex === 0 ? 'hidden' : undefined }}
          onClick={() => previous?.()}
        />
        Service {currentIndex + 1} of {services.length}
        <UtilityIconButton
          dark
          icon="chevron-right"
          disabled={currentIndex === carouselState!.totalItems - 1}
          sx={{
            visibility:
              currentIndex === carouselState!.totalItems - 1
                ? 'hidden'
                : undefined,
          }}
          onClick={() => next?.()}
        />
      </Flex>
    );
  };

  return (
    <Modal onClose={onClose} title={`Hazard Survey`}>
      <ModalBody>
        <Form pt={4}>
          <Box
            sx={{
              borderRadius: 'default',
              overflow: 'hidden',
              bg: 'tableHover',
            }}
          >
            <Carousel
              swipeable={true}
              draggable={false}
              arrows={false}
              showDots={false}
              renderDotsOutside={true}
              customButtonGroup={<ButtonGroup />}
              renderButtonGroupOutside={true}
              responsive={responsive}
              ssr={false} // means to render carousel on server-side.
              infinite={false}
              autoPlay={false}
              keyBoardControl={false}
              // customTransition="all .5"
              transitionDuration={500}
            >
              {services.map((service, index) => (
                <Flex
                  key={index}
                  flexDirection="column"
                  sx={{
                    flexGrow: 0,
                    // gap: 3,
                    padding: 4,
                    paddingTop: 0,
                  }}
                >
                  <FormRow gridTemplateColumns="1fr auto">
                    <FormColumn>
                      <Label>Service Account</Label>
                      <Box>
                        <Link
                          sx={{ color: 'primary' }}
                          target="_blank"
                          href={`/${mode}/water-services/${service.id}`}
                        >
                          {service.siteNumber}
                        </Link>
                      </Box>
                    </FormColumn>
                    <FormColumn>
                      <Label>Source</Label>
                      <Box>{constantToLabel(service.sourceType)}</Box>
                    </FormColumn>
                    <FormColumn>
                      <Label>Meter</Label>
                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {service.meterNumber}
                      </Box>
                    </FormColumn>
                    <FormColumn>
                      <Label>Usage</Label>
                      <Box>{constantToLabel(service.type)}</Box>
                    </FormColumn>
                  </FormRow>
                  <FormRow>
                    <FormColumn>
                      <Label>Hazards</Label>
                      <Box flexGrow={1}>
                        <WaterHazardsPicker
                          isClearable={false}
                          waterHazardIds={formValues?.waterServices?.[
                            servicePrefix + service.id
                          ]?.hazards?.map((hazard) => hazard.id)}
                          onChange={(waterHazards) => {
                            setServiceHazards(service.id, waterHazards);
                          }}
                        />
                      </Box>
                    </FormColumn>
                  </FormRow>
                  {formValues?.waterServices?.[servicePrefix + service.id]
                    ?.hazards &&
                    formValues.waterServices[servicePrefix + service.id].hazards
                      .length > 0 && (
                      <FormRow>
                        <FormColumn>
                          <Label>
                            Are all hazards protected for this service?
                          </Label>
                          <ButtonToggle
                            required={true}
                            name={`service${service.id}-isProtected`}
                            flexGrow={0}
                            value={
                              formValues?.waterServices?.[
                                servicePrefix + service.id
                              ]?.isProtected
                            }
                            onClick={(e: any) => {
                              setServiceIsProtected(
                                service.id,
                                e.target.value === 'false'
                                  ? false
                                  : e.target.value === 'true'
                                    ? true
                                    : !!e.target.value
                              );
                            }}
                          />
                        </FormColumn>
                      </FormRow>
                    )}
                  <FormRow>
                    <FormColumn>
                      <Label>Backflow Assemblies</Label>
                      {service.backflowAssemblies.length === 0 && (
                        <Box>None</Box>
                      )}
                      {service.backflowAssemblies.length > 0 && (
                        <Flex maxHeight={350}>
                          <DataTable
                            columns={assembliesColumns}
                            items={service.backflowAssemblies}
                            onRowClick={(assembly: ServiceAssembly) => {
                              if (assembly.id) {
                                window.open(
                                  `/${mode}/assemblies/${assembly.id}`
                                );
                              }
                            }}
                          />
                        </Flex>
                      )}
                    </FormColumn>
                  </FormRow>
                </Flex>
              ))}
            </Carousel>
          </Box>

          <FormRow mt={2}>
            <FormColumn>
              <Label>Comments</Label>
              <GrowyTextarea
                id="comments"
                required={hasUnprotectedHazards}
                placeholder="Enter comments"
                onChange={onFormInputChange}
                name="comments"
                value={formValues?.comments}
                focusedHeight={98}
                hasValueHeight={98}
                height={66}
                maxCharacters={255}
              />
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>Done</Button>
        {/*<Button icon="save" onClick={save}>*/}
        {/*  Save*/}
        {/*</Button>*/}
        {/*<Button ml={3} variant="outline" icon="slash-circle" onClick={onClose}>*/}
        {/*  Cancel*/}
        {/*</Button>*/}
      </ModalFooter>
    </Modal>
  );
};

export default HazardSurveyResultsModal;
